/* eslint-disable react/no-danger */

import { Component, memo, useEffect, useState } from 'react';
import Head from 'next/head';
import Script from 'next/script';

import EntireWorldLocale from 'locale/en/entire-world.json';
import MiscLocale from 'locale/en/misc.json';
import PdpLocale from 'locale/en/pdp.json';
import Link from 'next/link';

import Svg from 'lib/svgs.json';
import LegacyImage from 'next/legacy/image';
import Url from 'lib/siteurls';
import { apiGetCountries } from 'lib/api';
import SiteAccordion from 'components/site/site-accordion';
import SectionEntireWorldFaq from 'components/section/entire-world-faq';
import withVisibleHoc from 'components/misc/visible-hoc';
import cloudinaryLoader from 'lib/image/cloudinary-loader';
import styled, { createGlobalStyle } from 'styled-components';
import {
  Fontw5s12h18,
  ImgShadow,
  WithFontHead,
  WithSiteLayout,
} from 'styles/commonCss';
import { mqTabletMin, mqLargeMin } from 'styled/tools/media-queries';
import { isScreenLarge } from 'lib/utils/screen-size';
import { createMarkup } from 'lib/utils/create-markup';
import { pageReplaced } from 'lib/utils/page-replaced';
import { mqSmallMin, mqSmallMax } from 'styled/tools/media-queries';

const FootNoteContainer = styled.div`
  margin: 48px auto;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-top: 1px dotted var(--outline-neutral);
  padding-top: 24px;
`;

const Footnote = styled.div`
  ${Fontw5s12h18};
`;

const FootnoteLink = styled.a`
  ${Fontw5s12h18};
`;

// Antarctica left out intentionally
const continents = {
  AF: 'Africa',
  AS: 'Asia',
  EU: 'United Kingdom & Europe',
  NA: 'North America',
  OC: 'Australia / Oceania',
  SA: 'South America',
};
const imageHeightMobile = '235px';
const imageHeight = '297px';
const cardHeight = '647px';

const GlobalStyleEntireWorld = createGlobalStyle`
  .site-content {
    margin-top: var(--flag-margin);
    padding: 0;
    * {
      color: var(--text-primary);
    }
    > .container {
      max-width: 100%;
      width: 100%;
      padding: 0;
    }
    .s1 {
      position: relative;
      height: 100vh;
      width: 100%;
      @media ${mqLargeMin} {
        height: 670px;
      }
      * {
        color: #fff;
        stroke: #fff;
      }
      .banner {
        background-image: url('https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/w_1800/entire-world/image_hero_mobile.png');
        background-position: center;
        background-size: cover;
        position: relative;
        height: 100%;
        width: 100%;
        @media ${mqSmallMin} {
          background-image: url('https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/entire-world/image_hero_desktop.png');
        }
        .container {
          ${WithSiteLayout}
          display: flex;
          align-items: center;
          justify-content: flex-start;
          position: relative;
          height: 100%;
          width: 100%;
        }
        .poster {
          ${ImgShadow}
          background-repeat: no-repeat;
          background-position: center;
          background-size: auto 100%;
          background-image: url('https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/index/image_164_gpm6xv_l7rh1i.png');
          width: 120px;
          height: 120px;
        }
        .text {
          position: relative;
          z-index: 1;
          width: 400px;
          max-width: 100%;
          display: block;
          text-align: left;
        }
        .text .title {
          ${WithFontHead}
          margin: 0 0 var(--spacing-24); 
          font-size: 40px;
          line-height: 44px;
          @media ${mqSmallMin} {
            line-height: 1.2;
            font-size: var(--font-size-1200);
          }
        }
        .text .blurb {
          display: block;
          max-width: 100%;
          width: 300px;
        }
        .text button {
          display: flex;
          align-items: center;
          text-decoration: none;
          margin-top: var(--spacing-24);
          cursor: pointer;
          background: none;
          border: none;
        }
        .text button span {
          text-decoration: none;
          font-weight: 500;
          font-size: var(--font-size-300);
          margin-left: var(--spacing-8);
        }
        .text button i {
          transform: rotate(90deg);
          height: 25px;
        }
      }
    }
    .s2 {
      > .container {
        ${WithSiteLayout}
        padding-top: 55px;
        @media ${mqSmallMin} {
          padding-bottom: var(--spacing-48);
          padding-left: 170px;
          padding-right: 170px;
        }
      }

      .card {
        display: block;
        position: relative;
        text-align: center;
        padding: 10px 0 27px;

        @media ${mqSmallMin} {
          padding: 25px 0;
        }
      }
      .card > .container > .title {
        padding: 0 var(--spacing-24);
        font-size: var(--font-size-1000);
        text-align: center;
        margin: auto;
        line-height: 44px;
        @media ${mqSmallMin} {
          line-height: 55px;
        }
      }
      .card > .container > .blurb {
        display: block;
        margin-top: var(--spacing-24);
      }
      .card > .container > .media {
        display: flex;
        flex-direction: column;
        align-items: center;
        @media ${mqSmallMin} {
          margin: 0;
        }
        margin: 0 0 var(--spacing-24);
        overflow: hidden;
      }
      .card > .container > .text .head {
        ${WithFontHead}
        border-bottom: 1px solid var(--outline-primary);
        padding-bottom: 28px;
        margin-bottom: 28px;
        font-size: 1.875rem;
        text-align: left;
        line-height: 1;
        width: 100%;
      }
      .card > .container > .text .title {
        font-weight: 500;
        font-size: var(--font-size-400);
        margin-bottom: 25px;
        @media ${mqSmallMin} {
          font-weight: 500;
          text-align: left;
        }
      }
      .card > .container > .text .blurb {
        display: block;
        text-align: center;
        font-size: var(--font-size-400);
        @media ${mqSmallMin} {
          text-align: left;
        }
        &:last-child {
          margin-bottom: 0;
          font-size: var(--font-size-300);
          margin-top: 25px;
          @media ${mqSmallMin} {
            margin-top: 28px;
          }
        }
        &:last-child small {
          padding-right: var(--spacing-4);
          @media ${mqSmallMin} {
            padding-right: var(--spacing-4);
          }
        }
      }
      .card > .container > .text small {
        transform: translate(0px, -6px);
        font-size: var(--font-size-250);
        display: inline-block;
      }

      .card.c0 .container .title {
        max-width: 100%;
        margin: 0 auto;
        width: 640px;
        font-size: var(--font-size-1000);
        @media ${mqSmallMin} {
          font-size: var(--font-size-1200);
        }
      }
      .card.c0 .container .text {
        max-width: 100%;
        margin: 0 auto;
        width: 430px;
        p {
          text-align: center;
          margin-top: 30px;
        }
        p + p {
          margin-top: var(--spacing-8);
        }
      }

      .card.c1 {
        @media ${mqSmallMin} {
          padding: 3rem 100px 0;
        }
      }
      .card.c1 .container {
        @media ${mqSmallMin} {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
        }
      }
      .card.c1 .container > .text {
        @media ${mqSmallMin} {
          width: 45%;
        }
      }
      .card.c1 .container > .text .title {
        font-weight: 500;
      }
      .card.c1 .container > .media {
        @media ${mqSmallMin} {
          width: 45%;
        }
      }
      .card.c1 .container > .media img {
        @media ${mqSmallMax} {
          max-height: 235px;
        }
      }

      .card.c2 .container .title {
        font-size: var(--font-size-1000);
        @media ${mqSmallMin} {
          font-size: var(--font-size-1200);
        }
      }
      .card.c2 .container > .blurb {
        @media ${mqSmallMin} {
          max-width: 100%;
          margin: var(--spacing-24) auto 0;
          width: 500px;
        }
      }

      .card.c3 {
        @media ${mqSmallMax} {
          padding-top: 0;
        }
        @media ${mqSmallMin} {
          height: ${cardHeight};
          width: 37.5%;
        }
      }
      .card.c3 .media {
        max-height: ${imageHeightMobile};
        @media ${mqSmallMin} {
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin: 0 -50px;
          height: auto;
          max-height: ${imageHeight};
        }
      }
      .card.c3 .text {
        @media ${mqSmallMin} {
          margin-top: 28px;
          padding-top: 28px;
          border-top: 1px solid var(--outline-primary);
        }
      }
      .card.c3 img {
        height: auto;
        max-height: ${imageHeightMobile};
        @media ${mqSmallMin} {
          max-height: ${imageHeight};
        }
        &:not(:first-child) {
          margin-top: var(--spacing-8);
        }
      }

      .card.c4 {
        @media ${mqSmallMax} {
          padding-top: 0;
        }
        background-color: var(--fill-secondary);
        @media ${mqSmallMin} {
          height: ${cardHeight};
          width: 37.5%;
        }
      }
      .card.c4 .media {
        max-height: ${imageHeightMobile};
        margin: 0 -20px;

        @media ${mqSmallMin} {
          display: flex;
          flex-direction: column;
          justify-content: center;
          height: auto;
          max-height: ${imageHeight};
          margin: 0 -50px;
        }
      }
      .card.c4 .text {
        @media ${mqSmallMin} {
          margin-top: 30px;
          padding-top: 30px;
          border-top: 1px solid var(--outline-primary);
        }
      }
      .card.c4 img {
        height: auto;
        max-height: ${imageHeightMobile};
        @media ${mqSmallMin} {
          max-height: ${imageHeight};
        }
        &:not(:first-child) {
          margin-top: var(--spacing-8);
        }
      }

      .card.c5 {
        padding-bottom: 0;
        padding-top: 30px;
        @media ${mqSmallMin} {
          width: 500px;
          max-width: 100%;
          margin: 0 auto;
        }
      }
      .card.c5 .blurb {
        margin-top: 0;
      }
      .card.c5 .button {
        @media ${mqSmallMin} {
          max-width: 300px;
          margin: 35px auto 0;
          width: 100%;
        }
        width: 298px;
        margin: 25px auto 0;
        color: #fff;
      }

      .box {
        border-radius: 15px;
        overflow: hidden;
        border: 1px dotted var(--outline-primary);
        padding: 30px 20px 30px;
        @media ${mqSmallMin} {
          border-radius: 30px;
          padding: 40px 30px;
        }
      }
      .box > .container > .title {
        font-size: var(--font-size-600);
        margin-bottom: 14px;
        font-weight: 300;

        @media ${mqSmallMin} {
          font-size: var(--font-size-800);
          margin-bottom: var(--spacing-24);
        }
      }

      .row {
        @media ${mqSmallMin} {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
      }

      .button {
        text-transform: inherit;
        color: #fff;
      }
      .arrow {
        position: absolute;
        bottom: 0;
        left: -3px;
        width: 6px;
        height: 6px;
        border: solid var(--outline-primary);
        border-width: 0 1px 1px 0;
        transform: rotate(45deg);
      }

      .line {
        margin: auto;
        border-left: 1px dotted var(--outline-primary);
        position: relative;
        height: 61px;
        width: 1px;
      }
      .line.l3 {
        @media ${mqSmallMin} {
          height: calc(297px + 450px - 2px);
        }
      }
      .line.l2e {
        height: 127px;
        margin-top: -127px;
      }

      .lines {
        display: flex;
        align-items: center;
        position: relative;
        margin: 0 auto;
        width: 63.2%;
        @media ${mqSmallMax} {
          display: none;
        }
      }
      .lines.ls1 {
        background: #fff;
        justify-content: center;
      }
      .lines.ls2 {
        justify-content: space-between;
        height: 80px;
      }
      .lines .line {
        width: 25%;
        height: 81px;
        border: 1px dotted var(--outline-primary);
        margin: 0;
      }
      .lines .l2a {
        background: #fff;
        z-index: 1;
        border-top: 0;
        border-left: 0;
        height: 30px;
        border-radius: 0 0 30px 0;
        transform: translateX(0.5px);
      }
      .lines .l2b {
        background: #fff;
        z-index: 2;
        border-top: 0;
        border-right: 0;
        height: 30px;
        border-radius: 0 0 0 30px;
        transform: translateX(0.5px);
      }
      .lines .l2c {
        border-bottom: 0;
        border-right: 0;
        margin-top: -1px;
        border-radius: 25px 0 0 0;
      }
      .lines .l2d {
        border-bottom: 0;
        border-left: 0;
        margin-top: -1px;
        border-radius: 0 25px 0 0;
      }
    }
    .s3 {
      display: block;
      padding: 100px 0 68px;
      > .container {
        ${WithSiteLayout}
      }
      > .container > .title {
        ${WithFontHead}
        width: 100%;
        margin-bottom: 12px;
        @media ${mqLargeMin} {
          margin-bottom: var(--spacing-48);
        }
        font-size: 36px;
      }
      > .container > .media {
        display: block;
      }
      > .container > .text {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        padding: var(--spacing-48) 0 0;
        @media ${mqLargeMin} {
          justify-content: space-between;
          padding: 100px 0 70px;
        }
      }
      > .container > .text > .title {
        ${WithFontHead}
        width: 40%;
        font-size: var(--font-size-1000);
        @media ${mqSmallMin} {
          font-size: var(--font-size-1200);
        }
      }
      > .container > .text > .list {
        width: 100%;
        @media ${mqLargeMin} {
          width: 60%;
        }
      }
    }
    .s4 {
      display: block;
      padding: 64px 0 0;
      @media ${mqSmallMin} {
        padding: 150px 0 80px;
      }
      > .container {
        ${WithSiteLayout}
        text-align: center;
        font-size: 0;
      }
      .text {
        max-width: 100%;
        text-align: left;
        display: inline-block;
      }
      .text .title {
        ${WithFontHead}
        width: 730px;
        display: block;
        max-width: 100%;
        font-size: var(--font-size-600);
        font-weight: 300;
        line-height: 34px;
        @media ${mqSmallMin} {
          font-size: var(--font-size-1000);
          line-height: 55px;
        }
        @media ${mqLargeMin} {
          font-size: var(--font-size-1200);
          line-height: 55px;
        }
      }

      .text .title br {
        @media ${mqSmallMax} {
          display: none;
        }
      }
      .text .title img {
        vertical-align: text-top;
        height: auto;
        width: 30px;
        @media ${mqLargeMin} {
          transform: translateY(0);
          width: 46px;
        }
      }
      .text .blurb {
        margin-top: 42px;
        @media ${mqSmallMin} {
          margin-top: var(--spacing-48);
        }

        max-width: 100%;
        display: block;
        width: 525px;
      }
      .text .list {
        width: 400px;
        display: block;
        max-width: 100%;
        margin-top: 42px;
        margin-bottom: 20px;
        border-bottom: 1px dotted var(--outline-neutral);
        @media ${mqSmallMin} {
          margin-top: 35px;
          margin-bottom: 0px;
        }
      }
      .text .list li {
        display: block;
        padding: 12px 0;
        border-top: 1px dotted var(--outline-neutral);
        line-height: 23px;

        @media ${mqSmallMin} {
          padding: 12px 0;
        }
      }
    }
    .s5 {

      @media ${mqLargeMin} {
        height: 900px;
      }
      display: block;
      height: 860px;

      > .container {
        ${WithSiteLayout}
        overflow: hidden;
        position: relative;
        text-align: center;
        font-size: 0;
        height: 100%;
        @media ${mqSmallMax} {
          // This is to prevent excess transformations by decreasing .text padding
          margin-top: -26px;
        }
      }

      .media {
        transition: 'height 2s ease';
        transform: translateY(60px);
        background: #fff;
        position: absolute;
        overflow: hidden;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
      }
      .media .poster {
        padding-top: 30px;
        @media ${mqSmallMin} {
          padding-top: 0px;
        }
        @media ${mqLargeMin} {
          height: 900px;
        }
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        height: 860px;
        width: 100%;
        left: 0;
        top: 0;
      }
      .media.m1 [data-vimeo-initialized] {
        @media ${mqLargeMin} {
          transform: translate(-10px, 0);
          width: 415px !important;
          height: 415px;
        }
        @media ${mqSmallMin} {
          width: 50%;
        }
        width: 70%;
      }
      .media.m2 img {
        transform: scale(1.6);
        height: auto;
        @media ${mqLargeMin} {
          height: 700px;
        }
        @media ${mqSmallMin} {
          transform: scale(1);
        }
      }
      &.aos-animate .media.m2 {
        height: 0;
      }

      .text {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        text-align: left;
        padding: 100px 30px;
        @media ${mqLargeMin} {
          padding: 100px 50px;
        }
      }

      .text .title {
        ${WithFontHead}
        max-width: 280px;
        display: block;
        width: 100%;
        font-size: var(--font-size-1000);
        line-height: 44px;
        padding-bottom: 15px;
        @media ${mqLargeMin} {
          font-size: var(--font-size-1200);
          max-width: none;
          text-align: center;
        }
      }
      .text .blurb {
        ${WithFontHead}
        margin: var(--spacing-8) 0 0;
        display: block;
        max-width: 100%;
        width: 100%;
        font-size: var(--font-size-400);
        line-height: 24px;
        font-weight: 400;
        @media ${mqLargeMin} {
          margin: var(--spacing-48) 0 0;
          padding-top: 1.25rem;
          width: 380px;
          font-family: var(--flag-font-family);
          font-style: normal;
          font-weight: 300;
          font-size: 30px;
          line-height: 40px;

          color: #1c3a13;
        }
      }
      .text .blurb.b2 {
        margin: 0 0 100px 0;
        max-width: calc(100% - 60px);
        position: absolute;
        bottom: 0;
        right: var(--spacing-32);
      }
    }
  }
`;

const PageData = {
  page_type: 'default',
  page_class: 'page-entire-world',
  page_name: EntireWorldLocale.page_name,
  page_title: EntireWorldLocale.page_title,
  page_description: EntireWorldLocale.page_desc,
};
const PageHead = () => {
  const canon = 'https://seed.com/entire-world';
  return (
    <>
      <Head>
        <title>{`${MiscLocale.brand} • ${PageData.page_title}`}</title>
        <meta name='description' content={PageData.page_description} />
        <link rel='canonical' href={canon} />
      </Head>
      <Script
        src='https://www.google.com/recaptcha/api.js'
        strategy='afterInteractive'
      />
    </>
  );
};

type PageSection1Props = {
  handleAnchor: (anchor: string) => void;
};

const PageSection1 = memo((props: PageSection1Props) => (
  <section className='s1'>
    <div className='banner'>
      <div className='container'>
        <div className='text'>
          <h1 className='title'>{EntireWorldLocale.seeding_the}</h1>
          <button
            className='link'
            onClick={() => props.handleAnchor('#country-list')}
            type='button'
          >
            <i dangerouslySetInnerHTML={createMarkup(Svg.arrow_circle)} />
            <span>{EntireWorldLocale.where_we}</span>
          </button>
        </div>
      </div>
    </div>
  </section>
));

const PageSection2 = memo(() => (
  <section className='s2'>
    <div className='container'>
      <div className='card c0'>
        <div className='container'>
          <h2 className='title'>{EntireWorldLocale.unlock_free}</h2>
        </div>
      </div>
      <div className='line l0' />
      <div className='card box c1'>
        <div className='container'>
          <div className='title db dn-ns'>{EntireWorldLocale.your_first}</div>
          <div className='media'>
            <LegacyImage
              src={`${Url.cloudinary}/f_auto,q_auto/entire-world/Seed_Packaging_WelcomeKit_Transparent_jrypao_sokr40.png`}
              width={351}
              height={415}
              alt={EntireWorldLocale.unlock_free}
              loader={cloudinaryLoader}
            />
          </div>
          <div className='text'>
            <h3 className='head dn db-ns'>{EntireWorldLocale.your_first}</h3>
            <p className='title'>{PdpLocale.welcome_kit}</p>
            <p className='blurb'>
              $10 flat rate worldwide (USD)<small>1</small>
            </p>
            <p className='blurb'>Free US shipping</p>
            <p className='blurb'>
              <small>1</small>Local taxes apply
            </p>
          </div>
        </div>
      </div>
      <div className='line l1' />
      <div className='card c2'>
        <div className='container'>
          <h2 className='title'>Refill Options</h2>
          <p className='blurb'>
            After your first month, you may continue with monthly refills
            shipped flat rate, or enroll in our Sustainable Refill Program* for
            90-day refills, billed every 3 months, shipped free.
          </p>
        </div>
      </div>
      <div className='line l2 db dn-ns' />
      <div className='line l2f dn db-ns' />
      <div className='lines ls1 dn db-ns'>
        <div className='line l2a' />
        <div className='line l2b' />
      </div>
      <div className='lines ls2 dn db-ns'>
        <div className='line l2c' />
        <div className='line l2d' />
      </div>
      <div className='line l2e dn db-ns' />
      <div className='row'>
        <div className='card box c3'>
          <div className='container'>
            <div className='media'>
              <LegacyImage
                src={`${Url.cloudinary}/f_auto,q_auto/entire-world/image_refill_standard.png`}
                width={196}
                height={297}
                alt='Standard Monthly Refill'
                loader={cloudinaryLoader}
              />
            </div>
            <div className='text'>
              <h3 className='title'>Standard Monthly Refill</h3>
              <p className='blurb'>
                $10 flat rate worldwide (USD)<small>1</small>
              </p>
              <p className='blurb'>30-day refills, billed monthly</p>
              <p className='blurb'>
                <small>1</small>Local taxes apply
              </p>
            </div>
          </div>
        </div>
        <div className='line l3' />
        <div className='card box c4'>
          <div className='container'>
            <div className='media'>
              <LegacyImage
                src={`${Url.cloudinary}/f_auto,q_auto/entire-world/image_refill_multiple.png`}
                width={397}
                height={297}
                alt='Sustainable Refill Program'
                loader={cloudinaryLoader}
              />
            </div>
            <div className='text'>
              <h3 className='title'>Sustainable Refill Program</h3>
              <p className='blurb'>
                Free shipping worldwide<small>1</small>
              </p>
              <p className='blurb'>90-day refills, billed every 3 months</p>
              <p className='blurb'>
                <small>1</small>Local taxes apply
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className='line l4'>
        <i className='arrow' />
      </div>
      <div className='card c5'>
        <div className='container'>
          <p className='blurb'>
            Fewer shipments mean 26% less transport emissions,<sup>2</sup> a win
            for both people and the planet.
          </p>
          <Link href={Url.shop} prefetch={false} className='button'>
            {EntireWorldLocale.subscribe_now}
          </Link>
        </div>
      </div>
    </div>
  </section>
));

type Section3Props = {
  continentMap: object;
};
const PageSection3 = memo(({ continentMap }: Section3Props) => (
  <section className='s3'>
    <div id='country-list' className='container'>
      <h2 className='title db dn-ns'>{EntireWorldLocale.where_we} —</h2>
      <div className='media'>
        <div className='db dn-ns'>
          <LegacyImage
            src={`${Url.cloudinary}/f_auto,q_auto/entire-world/image_ship_mobile.png`}
            width={960}
            height={825}
            alt={EntireWorldLocale.where_we}
            layout='responsive'
            loader={cloudinaryLoader}
          />
        </div>
        <div className='dn db-ns'>
          <LegacyImage
            src={`${Url.cloudinary}/f_auto,q_auto/entire-world/image_ship_desktop.png`}
            width={1800}
            height={969}
            alt={EntireWorldLocale.where_we}
            layout='responsive'
            loader={cloudinaryLoader}
          />
        </div>
      </div>
      <div id='country-list-desktop' className='text'>
        <h2 className='title dn db-ns'>{EntireWorldLocale.where_we} —</h2>
        <div className='list'>
          {Object.keys(continents).map((continentKey, index) => (
            <SiteAccordion
              key={continentKey}
              title={continents[continentKey]}
              index={index}
            >
              <ul>
                {continentMap[continentKey]?.length ? (
                  continentMap[continentKey].map((country) => (
                    <li key={country.name}>{country.name}</li>
                  ))
                ) : (
                  <p
                    dangerouslySetInnerHTML={createMarkup(
                      EntireWorldLocale.unavailable,
                    )}
                  />
                )}
              </ul>
            </SiteAccordion>
          ))}
        </div>
      </div>
    </div>
  </section>
));
const PageSection4 = memo(() => {
  const currentYear = new Date().getFullYear();
  const in1969String = EntireWorldLocale.in_1969.replace('{year}', currentYear);
  return (
    <section className='s4'>
      <div className='container'>
        <div className='text'>
          <div
            className='title'
            dangerouslySetInnerHTML={createMarkup(in1969String)}
          />
          <div className='blurb'>{EntireWorldLocale.luckily_our}</div>
          <ul className='list'>
            <li>{EntireWorldLocale.real_time}</li>
            <li>{EntireWorldLocale.shipment_notifications}</li>
            <li>{EntireWorldLocale.priority_chat}</li>
          </ul>
        </div>
      </div>
    </section>
  );
});

const VideoStyled = styled.video`
  position: absolute;

  width: 50%;

  @media ${mqTabletMin} {
    width: 40%;
  }

  @media ${mqLargeMin} {
    width: 25%;
  }
`;
// TSREVIEW:
// @ts-ignore
const PageSection5 = withVisibleHoc(({ visible }, ref) => {
  const [paused, setPaused] = useState(true);

  useEffect(() => {
    if (visible) {
      setPaused(false);
    }
  }, [visible, paused]);

  return (
    // TSREVIEW:
    // @ts-ignore
    <section className='s5' ref={ref}>
      <div className='container'>
        <div className='media m1'>
          <div className='poster'></div>
        </div>
        <div className='media m2'>
          <div className='poster'>
            <VideoStyled playsInline webkit-playsInline autoPlay loop muted>
              <source
                src={`https://res.cloudinary.com/dljz0lko8/video/upload/v1748058550/index/globe-spinning-_2x.mp4`}
                type='video/mp4'
              />
            </VideoStyled>
          </div>
        </div>
        <div className='text'>
          <h2 className='title'>{EntireWorldLocale.the_human}</h2>
          <div
            className='blurb b1 aos-trigger'
            dangerouslySetInnerHTML={createMarkup(
              EntireWorldLocale.international_shipping,
            )}
          ></div>
          <div className='blurb b2'>{EntireWorldLocale.as_we}</div>
        </div>
      </div>
    </section>
  );
});

type PageEntireWorldProps = {
  continentMap: object;
};

type PageEntireWorldState = {};

class PageEntireWorld extends Component<
  PageEntireWorldProps,
  PageEntireWorldState
> {
  constructor(props) {
    super(props);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleAnchor = this.handleAnchor.bind(this);
  }

  async componentDidMount() {
    pageReplaced(PageData);
    document.querySelector('html').classList.add('with-header-contrast');
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    const siteHtml = document.querySelector('html');
    siteHtml.classList.remove('with-header-contrast');
  }

  handleScroll() {
    if (window.scrollY === 0) {
      document.querySelector('html').classList.add('with-header-contrast');
    } else {
      document.querySelector('html').classList.remove('with-header-contrast');
    }
  }

  handleAnchor(id) {
    const main = isScreenLarge()
      ? document.querySelector<HTMLElement>('#country-list-desktop')
      : document.querySelector<HTMLElement>(id);
    const head = document.querySelector<HTMLElement>('header');

    const mainTop = main.offsetTop - head.offsetHeight + 1;
    window.scrollTo({ top: mainTop, left: 0, behavior: 'smooth' });
    if (main) {
      const focusable = main.querySelector<HTMLElement>(
        'a[href],button,*[tabindex]',
      );
      if (focusable) focusable.focus();
    }
  }

  render() {
    const { continentMap } = this.props;

    return (
      <>
        <GlobalStyleEntireWorld />
        <PageHead />
        <div className='container'>
          <PageSection1 handleAnchor={this.handleAnchor} />
          <PageSection2 />
          <PageSection3 continentMap={continentMap} />
          <PageSection4 />
          <PageSection5
            withAnchor
            // TSREVIEW:
            // prop onVisible isn't a boolean
            // @ts-ignore
            onVisible
            checkOnlyVisible
          />
          <FootNoteContainer>
            <Footnote>
              <sup>2</sup> Based on our 2023 corporate GHG inventory and
              product-level lifecycle assessments, and verified by our partner{' '}
              <FootnoteLink href='https://www.planetfwd.com/about'>
                Planet FWD
              </FootnoteLink>
              .
            </Footnote>
            <Footnote>
              <sup>3</sup> Fisher, L. (n.d.). What tech would the Apollo 11
              mission have today?.{' '}
              <FootnoteLink href='https://www.sciencefocus.com/space/what-tech-would-the-apollo-11-mission-have-today>'>
                BBC Science Focus Magazine.
              </FootnoteLink>
            </Footnote>
            <Footnote>
              <sup>4</sup> Tiseo, I. (2024b, September 5). Global shipping co₂
              emissions 1970-2023.{' '}
              <FootnoteLink href='https://www.statista.com/statistics/1291468/international-shipping-emissions-worldwide/#:~:text=International%20shipping%20carbon%20dioxide%20emissions,19%2Drelated%20slump%20in%202020'>
                Statista.
              </FootnoteLink>
            </Footnote>
            <Footnote>
              <sup>5</sup> Tiseo, I. (2024, September 4). Shipping emissions
              worldwide.{' '}
              <FootnoteLink href='https://www.statista.com/topics/11288/shipping-emissions-worldwide/#topicOverview'>
                Statista.
              </FootnoteLink>
            </Footnote>
          </FootNoteContainer>
          <SectionEntireWorldFaq title={EntireWorldLocale.questions_we} />
        </div>
      </>
    );
  }
}

export async function getStaticProps() {
  const continentMap = {};
  try {
    const countries = await apiGetCountries('shipping');
    // Creates a map of continents to countries in that continent
    Object.keys(continents).forEach((continentCode) => {
      continentMap[continentCode] = countries.filter(
        (country) => continentCode === country.continent_code,
      );
    });
  } catch {
    // no exception handling
  }

  return {
    props: { continentMap },
    revalidate: 60 * 60, // 1 hour in seconds
  };
}

export default PageEntireWorld;
